import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package } from 'lucide-react';
import Footer from '../components/layout/footer';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../lib/supabase';

interface OrderItem {
  id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  image_url: string | null;
}

interface Order {
  id: string;
  order_number: string | null;
  status: string;
  payment_status: string;
  total_amount: number;
  currency: string | null;
  created_at: string;
  order_items?: OrderItem[];
}

export default function MyOrdersPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
        return;
      }
      const { data: ordersData, error } = await supabase
        .from('orders')
        .select('id, order_number, status, payment_status, total_amount, currency, created_at')
        .eq('profile_id', session.user.id)
        .order('created_at', { ascending: false });
      if (error) {
        setOrders([]);
        setLoading(false);
        return;
      }
      const withItems = await Promise.all(
        (ordersData || []).map(async (o) => {
          const { data: items } = await supabase
            .from('order_items')
            .select('id, product_name, quantity, unit_price, total_price, image_url')
            .eq('order_id', o.id);
          return { ...o, order_items: items || [] };
        })
      );
      setOrders(withItems);
      setLoading(false);
    };
    load();
  }, [navigate]);

  const formatDate = (s: string) => {
    try {
      return new Date(s).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return s;
    }
  };

  const formatAmount = (amount: number, currency: string | null) => {
    const c = currency || 'XOF';
    return `${Number(amount).toLocaleString()} ${c}`;
  };

  const statusLabel: Record<string, string> = {
    pending: 'En attente',
    confirmed: 'Confirmée',
    preparing: 'En préparation',
    shipped: 'Expédiée',
    delivered: 'Livrée',
    cancelled: 'Annulée',
    refunded: 'Remboursée',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <div className="pt-24 md:pt-28 pb-20 max-w-3xl mx-auto px-6">
        <h1 className="font-['Sora'] text-2xl md:text-3xl font-light tracking-tight mb-8">
          {t('account.my_orders')}
        </h1>
        {orders.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center">
            <Package className="mx-auto h-12 w-12 text-white/30 mb-4" />
            <p className="font-['Rubik'] text-white/60">{t('account.orders_empty')}</p>
            <Link
              to="/collections"
              className="mt-6 inline-block font-['Rubik'] text-sm text-white underline hover:no-underline"
            >
              Découvrir la collection
            </Link>
          </div>
        ) : (
          <ul className="space-y-4">
            {orders.map((order) => (
              <li
                key={order.id}
                className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden"
              >
                <div className="p-4 md:p-6 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="font-['Sora'] font-medium text-white">
                      {t('account.order_number')} {order.order_number || order.id.slice(0, 8)}
                    </p>
                    <p className="font-['Rubik'] text-sm text-white/50 mt-1">
                      {t('account.order_date')} : {formatDate(order.created_at)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-['Rubik'] text-white/70">
                      {statusLabel[order.status] || order.status}
                    </p>
                    <p className="font-['Sora'] text-lg text-white mt-1">
                      {formatAmount(Number(order.total_amount), order.currency)}
                    </p>
                  </div>
                </div>
                {order.order_items && order.order_items.length > 0 && (
                  <div className="border-t border-white/10 px-4 md:px-6 py-3 flex gap-3 overflow-x-auto">
                    {order.order_items.slice(0, 5).map((item) => (
                      <div
                        key={item.id}
                        className="shrink-0 w-14 h-14 rounded-lg bg-white/10 overflow-hidden flex items-center justify-center"
                      >
                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt={item.product_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Package className="w-6 h-6 text-white/30" />
                        )}
                      </div>
                    ))}
                    {order.order_items.length > 5 && (
                      <span className="shrink-0 flex items-center text-white/40 text-sm">
                        +{order.order_items.length - 5}
                      </span>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <Footer />
    </div>
  );
}
